import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AlertCircle, KeyRound, Loader2, Mail, Pencil, Plus, RefreshCw, Trash2, Users, X } from 'lucide-react';
import {
  Avatar, Badge, Button, Card, CardContent, Dialog, Input, PageHeader, Select, Table, TBody, Td, Th, THead, Tr, cn,
} from '../ui';
import ConfirmacionEliminar from '../ui/ConfirmacionEliminar';
import PersonaPickerMiembro from '../incorporaciones/components/PersonaPickerMiembro';
import {
  TIPOS_CUENTA, anadirPersonaCuenta, borrarCuenta, buscarPersonasParaCuenta, crearCuenta, editarCuenta,
  listarCuentas, mensajeDeError, opcionesCuentas, quitarPersonaCuenta,
} from './api';

/**
 * Personas › Buzones y listas (30/10). La pantalla de las cuentas compartidas, que hasta hoy vivía en
 * la configuración de Incorporaciones (`incorporaciones/CuentasCompartidas.jsx`, retirada). Mismos
 * datos (`incorp_cuentas_compartidas`), otra casa: las cuentas son de Personas, y apagar
 * Incorporaciones no puede apagarlas.
 *
 *   · Tres pestañas por tipo. La pestaña va en la URL (`?tipo=`) para poder enlazarla.
 *   · Un miembro es un miembro: sin niveles de permiso por persona (fuera de alcance).
 *   · Los miembros se guardan AL MOMENTO; los datos de la cuenta, con «Guardar». El diálogo lo dice.
 *   · Los errores del servidor (409 «Ese correo ya es el principal de…») se ven DENTRO del
 *     formulario, no en un toast que desaparece: es ahí donde hay que corregir.
 *
 * Ruta y entrada de menú con `personas.correos.gestionar`; el servidor lo vuelve a exigir.
 */

const PESTANAS = ['buzon', 'lista', 'licencia'];
const ICONO = { buzon: Mail, lista: Users, licencia: KeyRound };
const MAX_AVATARES = 5;

const nombreCompleto = (p) => p.nombre || `#${p.id}`;

function Miembros({ personas }) {
  if (!personas.length) return <span className="text-xs text-slate-400">Nadie</span>;
  const visibles = personas.slice(0, MAX_AVATARES);
  return (
    <div className="flex items-center gap-2">
      <div className="flex -space-x-2">
        {visibles.map((p) => (
          <Avatar key={`${p.ambito}-${p.id}`} name={nombreCompleto(p)} size="sm" className="ring-2 ring-white" title={nombreCompleto(p)} />
        ))}
      </div>
      <span className="text-xs text-slate-500">
        {personas.length} {personas.length === 1 ? 'persona' : 'personas'}
      </span>
    </div>
  );
}

function Campo({ etiqueta, ayuda, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-slate-600">{etiqueta}</span>
      {children}
      {ayuda && <span className="mt-1 block text-[11px] text-slate-400">{ayuda}</span>}
    </label>
  );
}

/** Los datos de la cuenta, en el estado del formulario. */
const formDe = (cuenta, tipoInicial) => ({
  correo: cuenta ? cuenta.correo : '',
  nombre: cuenta ? cuenta.nombre || '' : '',
  tipo: cuenta ? cuenta.tipo : tipoInicial,
  descripcion: cuenta ? cuenta.descripcion || '' : '',
  area_id: cuenta && cuenta.area ? String(cuenta.area.id) : '',
  inv_asset_type_id: cuenta && cuenta.pieza ? String(cuenta.pieza.tipo_id) : '',
  referencia: cuenta && cuenta.pieza ? cuenta.pieza.referencia || '' : '',
  activo: cuenta ? cuenta.activo : true,
});

function CuentaDialog({ cuenta, tipoInicial, opciones, onClose, onCreada, onGuardada, onMiembrosCambiados }) {
  const esNueva = !cuenta;
  const [form, setForm] = useState(() => formDe(cuenta, tipoInicial));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [ambito, setAmbito] = useState('interno');
  const [ocupado, setOcupado] = useState(false);
  const [errorMiembros, setErrorMiembros] = useState('');

  // El formulario se rellena al MONTAR (el padre cambia la `key` al pasar de nueva a creada), no en
  // cada recarga: añadir un miembro recarga la lista y no debe borrar lo que se esté escribiendo.

  const cambiar = (campo) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [campo]: v }));
  };

  const guardar = async () => {
    setGuardando(true);
    setError('');
    const esLicencia = form.tipo === 'licencia';
    const payload = {
      correo: form.correo, nombre: form.nombre, tipo: form.tipo, descripcion: form.descripcion,
      area_id: form.area_id ? Number(form.area_id) : null,
      // La pieza sólo tiene sentido en una licencia; en un buzón o una lista se desenlaza.
      inv_asset_type_id: esLicencia && form.inv_asset_type_id ? Number(form.inv_asset_type_id) : null,
      referencia: esLicencia ? form.referencia.trim() || null : null,
      ...(esNueva ? {} : { activo: form.activo }),
    };
    try {
      if (esNueva) {
        const r = await crearCuenta(payload);
        toast.success('Creada. Ahora añade quién está detrás.');
        await onCreada(r.id);
      } else {
        await editarCuenta(cuenta.id, payload);
        toast.success('Guardado.');
        onGuardada();
      }
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo guardar. Revisa la conexión y vuelve a intentarlo.'));
    } finally {
      setGuardando(false);
    }
  };

  const buscar = useCallback((q) => buscarPersonasParaCuenta(q, ambito), [ambito]);
  const yaEstan = cuenta ? cuenta.personas.filter((p) => p.ambito === ambito).map((p) => p.id) : [];

  const anadir = async (p) => {
    setOcupado(true);
    setErrorMiembros('');
    try { await anadirPersonaCuenta(cuenta.id, ambito, p.id); await onMiembrosCambiados(); } catch (e) {
      setErrorMiembros(mensajeDeError(e, 'No se pudo añadir a esa persona.'));
    } finally { setOcupado(false); }
  };
  const quitar = async (p) => {
    setOcupado(true);
    setErrorMiembros('');
    try { await quitarPersonaCuenta(cuenta.id, p.ambito, p.id); await onMiembrosCambiados(); } catch (e) {
      setErrorMiembros(mensajeDeError(e, 'No se pudo quitar a esa persona.'));
    } finally { setOcupado(false); }
  };

  const titulo = esNueva ? `Nuevo: ${TIPOS_CUENTA[form.tipo].label.toLowerCase()}` : (cuenta.nombre || cuenta.correo);

  return (
    <Dialog
      open
      size="lg"
      onClose={() => !guardando && !ocupado && onClose()}
      title={titulo}
      description={esNueva ? 'Primero la dirección; después, quién está detrás.' : cuenta.correo}
      footer={(
        <>
          <Button variant="outline" onClick={onClose} disabled={guardando}>{esNueva ? 'Cancelar' : 'Cerrar'}</Button>
          <Button onClick={guardar} disabled={guardando || !form.correo.trim() || !form.nombre.trim()}>
            {guardando && <Loader2 className="size-4 animate-spin" />}
            {esNueva ? 'Crear' : 'Guardar cambios'}
          </Button>
        </>
      )}
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Campo etiqueta="Dirección" ayuda="Se guarda en minúsculas y sin espacios.">
            <Input value={form.correo} onChange={cambiar('correo')} placeholder="soporte@grupolae.net" aria-label="Dirección" autoFocus={esNueva} />
          </Campo>
          <Campo etiqueta="Nombre visible">
            <Input value={form.nombre} onChange={cambiar('nombre')} placeholder="Soporte IT" aria-label="Nombre visible" maxLength={150} />
          </Campo>
          <Campo etiqueta="Tipo">
            <Select value={form.tipo} onChange={cambiar('tipo')} aria-label="Tipo">
              {Object.entries(TIPOS_CUENTA).map(([v, t]) => <option key={v} value={v}>{t.label}</option>)}
            </Select>
          </Campo>
          <Campo etiqueta="Área que quita el acceso en una baja" ayuda="Sin área, la baja usa la del recurso de su pieza, si la tiene.">
            <Select value={form.area_id} onChange={cambiar('area_id')} aria-label="Área">
              <option value="">Sin área</option>
              {opciones.areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </Select>
          </Campo>
          {form.tipo === 'licencia' && (
            <>
              <Campo etiqueta="Pieza de Inventario: tipo" ayuda="Sólo si la licencia es una pieza de Inventario.">
                <Select value={form.inv_asset_type_id} onChange={cambiar('inv_asset_type_id')} aria-label="Tipo de Inventario">
                  <option value="">Sin pieza</option>
                  {opciones.tipos.map((x) => <option key={x.id} value={x.id}>{x.nombre} ({x.scope === 'colab' ? 'colaboradoras' : 'internos'})</option>)}
                </Select>
              </Campo>
              <Campo etiqueta="Pieza de Inventario: referencia">
                <Input value={form.referencia} onChange={cambiar('referencia')} placeholder="LC-0051" aria-label="Referencia de la pieza" disabled={!form.inv_asset_type_id} />
              </Campo>
            </>
          )}
          <div className="sm:col-span-2">
            <Campo etiqueta="Descripción">
              <Input value={form.descripcion} onChange={cambiar('descripcion')} placeholder="Para qué se usa" aria-label="Descripción" maxLength={200} />
            </Campo>
          </div>
          {!esNueva && (
            <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
              <input type="checkbox" checked={form.activo} onChange={cambiar('activo')} />
              Activa (una inactiva no sale en el buscador ni genera tareas en la baja)
            </label>
          )}
        </div>

        {error && (
          <p role="alert" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />{error}
          </p>
        )}

        {!esNueva && (
          <section className="border-t border-slate-100 pt-4">
            <div className="mb-2 flex items-baseline justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Quién está detrás ({cuenta.personas.length})</h3>
              <span className="text-[11px] text-slate-400">Se guarda al momento</span>
            </div>
            {cuenta.personas.length === 0 ? (
              <p className="mb-3 text-xs text-slate-500">Nadie todavía.</p>
            ) : (
              <ul className="mb-3 divide-y divide-slate-100 rounded-md border border-slate-200">
                {cuenta.personas.map((p) => (
                  <li key={`${p.ambito}-${p.id}`} className="flex items-center gap-3 px-3 py-2">
                    <Avatar name={nombreCompleto(p)} size="sm" />
                    <span className="flex-1 text-sm text-slate-800">{nombreCompleto(p)}</span>
                    {p.ambito === 'colaborador' && <Badge variant="secondary">Colaboradora</Badge>}
                    <Button variant="ghost" size="icon" onClick={() => quitar(p)} disabled={ocupado} aria-label={`Quitar a ${nombreCompleto(p)}`} title="Quitar">
                      <X className="size-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mb-2 flex gap-1" role="group" aria-label="Ámbito de la persona que se añade">
              {[['interno', 'Internos'], ['colaborador', 'Colaboradoras']].map(([v, l]) => (
                <Button key={v} size="sm" variant={ambito === v ? 'primary' : 'outline'} aria-pressed={ambito === v} onClick={() => setAmbito(v)}>{l}</Button>
              ))}
            </div>
            <PersonaPickerMiembro onAdd={anadir} excluirIds={yaEstan} disabled={ocupado} buscar={buscar} enFlujo
              placeholder={ambito === 'interno' ? 'Añadir persona interna: nombre, apellidos o correo…' : 'Añadir colaboradora…'} />
            {errorMiembros && (
              <p role="alert" className="mt-2 flex items-start gap-2 text-sm text-red-700">
                <AlertCircle className="mt-0.5 size-4 shrink-0" />{errorMiembros}
              </p>
            )}
          </section>
        )}
      </div>
    </Dialog>
  );
}

export default function BuzonesYListas() {
  const [params, setParams] = useSearchParams();
  const pestana = PESTANAS.includes(params.get('tipo')) ? params.get('tipo') : 'buzon';
  const [cuentas, setCuentas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');
  const [opciones, setOpciones] = useState({ areas: [], tipos: [] });
  const [editor, setEditor] = useState(null); // { id } | { nueva: true }
  const [aBorrar, setABorrar] = useState(null);

  const cargar = useCallback(async () => {
    setErrorCarga('');
    try { setCuentas(await listarCuentas()); } catch (e) {
      setErrorCarga(mensajeDeError(e, 'No se pudieron cargar las cuentas.'));
    } finally { setCargando(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    opcionesCuentas().then(setOpciones).catch((e) => {
      toast.error(mensajeDeError(e, 'No se pudieron cargar las áreas y los tipos de Inventario.'));
    });
  }, []);

  const porTipo = useMemo(() => {
    const m = { buzon: [], lista: [], licencia: [] };
    cuentas.forEach((c) => { if (m[c.tipo]) m[c.tipo].push(c); });
    return m;
  }, [cuentas]);
  const visibles = porTipo[pestana];
  const t = TIPOS_CUENTA[pestana];
  const Icono = ICONO[pestana];
  const cuentaEnEditor = editor && editor.id ? cuentas.find((c) => c.id === editor.id) : null;

  const borrar = async () => {
    try {
      await borrarCuenta(aBorrar.id);
      toast.success(`${aBorrar.correo} borrado.`);
      setABorrar(null);
      await cargar();
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo borrar.'));
    }
  };

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Buzones y listas"
        description="Direcciones de la organización y las personas que hay detrás. El buscador de Personas las encuentra por su dirección o su nombre (las licencias, no)."
        actions={<Button onClick={() => setEditor({ nueva: true })}><Plus className="size-4" />Nuevo: {t.corto.toLowerCase()}</Button>}
      />

      <div role="tablist" aria-label="Tipo" className="flex gap-1 border-b border-slate-200">
        {PESTANAS.map((p) => (
          <button key={p} type="button" role="tab" aria-selected={pestana === p}
            onClick={() => setParams({ tipo: p }, { replace: true })}
            className={cn('-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition-colors',
              pestana === p ? 'border-[hsl(var(--brand-light))] font-semibold text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800')}>
            {TIPOS_CUENTA[p].pestana}
            <Badge variant={pestana === p ? 'primary' : 'secondary'}>{porTipo[p].length}</Badge>
          </button>
        ))}
      </div>

      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500"><Loader2 className="size-5 animate-spin" />Cargando…</div>
      ) : errorCarga ? (
        <Card><CardContent className="py-12 text-center">
          <AlertCircle className="mx-auto mb-2 size-6 text-red-500" />
          <p className="mb-4 text-sm text-red-600">{errorCarga}</p>
          <Button variant="outline" onClick={cargar}><RefreshCw className="size-4" />Reintentar</Button>
        </CardContent></Card>
      ) : visibles.length === 0 ? (
        <Card><CardContent className="py-12 text-center">
          <Icono className="mx-auto mb-2 size-6 text-slate-400" />
          <p className="text-sm text-slate-600">Todavía no hay ningún {t.corto.toLowerCase()}.</p>
          <Button className="mt-4" onClick={() => setEditor({ nueva: true })}><Plus className="size-4" />Nuevo: {t.corto.toLowerCase()}</Button>
        </CardContent></Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <THead>
              <Tr>
                <Th>Dirección</Th>
                <Th>Nombre</Th>
                <Th>Miembros</Th>
                <Th align="right"><span className="sr-only">Acciones</span></Th>
              </Tr>
            </THead>
            <TBody>
              {visibles.map((c) => (
                <Tr key={c.id} className={cn(!c.activo && 'opacity-60')}>
                  <Td>
                    <button type="button" onClick={() => setEditor({ id: c.id })} className="break-all text-left font-medium text-slate-900 hover:underline">
                      {c.correo}
                    </button>
                    {!c.activo && <Badge variant="secondary" className="ml-2">Inactiva</Badge>}
                  </Td>
                  <Td>
                    <div className="text-slate-800">{c.nombre}</div>
                    {c.descripcion && <div className="text-xs text-slate-500">{c.descripcion}</div>}
                  </Td>
                  <Td><Miembros personas={c.personas} /></Td>
                  <Td align="right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setEditor({ id: c.id })} aria-label={`Editar ${c.correo}`} title="Editar"><Pencil className="size-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setABorrar(c)} aria-label={`Borrar ${c.correo}`} title="Borrar"><Trash2 className="size-4" /></Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      {editor && (editor.nueva || cuentaEnEditor) && (
        <CuentaDialog
          key={editor.nueva ? 'nueva' : editor.id}
          cuenta={cuentaEnEditor}
          tipoInicial={pestana}
          opciones={opciones}
          onClose={() => setEditor(null)}
          onCreada={async (id) => { await cargar(); setEditor({ id }); }}
          onGuardada={async () => { setEditor(null); await cargar(); }}
          onMiembrosCambiados={cargar}
        />
      )}

      <ConfirmacionEliminar
        open={!!aBorrar}
        onClose={() => setABorrar(null)}
        onConfirm={borrar}
        titulo={aBorrar ? `Borrar ${aBorrar.correo}` : 'Borrar'}
        descripcion={aBorrar
          ? `Se borra «${aBorrar.nombre}» y la lista de quién está detrás (${aBorrar.personas.length}). Ninguna baja volverá a avisar de quitar el acceso a esta dirección. La pieza de Inventario, si la tiene, no se toca.`
          : ''}
        textoBotonConfirmar="Borrar"
      />
    </div>
  );
}
